import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { FileText, Download, Loader2, Trash2, Upload, Globe, Lock } from 'lucide-react';
import { fmtSize } from './BookingDocuments';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Provider documents library — waivers, price sheets, insurance certs; optionally shared publicly.
export default function ProviderDocumentsCard() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const fileRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/documents`, { headers });
      setDocs(res.data || []);
    } catch { /* optional */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/dashboard/documents`, fd, { headers });
      toast.success('Document added to your library');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const toggleShared = async (d) => {
    try {
      await axios.patch(`${API_URL}/api/dashboard/documents/${d.id}`, { sharedWithClients: !d.sharedWithClients }, { headers });
      setDocs((prev) => prev.map((x) => x.id === d.id ? { ...x, sharedWithClients: !d.sharedWithClients } : x));
      toast.success(!d.sharedWithClients ? 'Now visible on your public profile' : 'Hidden from clients');
    } catch { toast.error('Could not update document'); }
  };

  const download = async (d) => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard/documents/${d.id}/file`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = d.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not download document'); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title || d.filename}" from your library?`)) return;
    try {
      await axios.delete(`${API_URL}/api/dashboard/documents/${d.id}`, { headers });
      setDocs((prev) => prev.filter((x) => x.id !== d.id));
      toast.success('Document deleted');
    } catch { toast.error('Could not delete document'); }
  };

  return (
    <Card data-testid="provider-documents-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Documents Library
        </CardTitle>
        <CardDescription>
          Store waivers, price sheets, and insurance certs. Flip "Share" to show a document on your public profile for clients to download. PDF, images, Office docs — up to 10MB each.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <input ref={fileRef} type="file" className="hidden" onChange={upload}
          accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt"
          data-testid="library-file-input" />
        <Button variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="library-upload-btn">
          {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          Add Document
        </Button>
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : docs.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="library-empty">No documents yet.</p>
        ) : (
          <ul className="space-y-2">
            {docs.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm" data-testid={`library-doc-${d.id}`}>
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-[160px]">
                  <p className="font-medium truncate">{d.title || d.filename}</p>
                  <p className="text-xs text-muted-foreground">{d.filename} · {fmtSize(d.size)}</p>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  {d.sharedWithClients ? <Globe className="h-3.5 w-3.5 text-emerald-600" /> : <Lock className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">{d.sharedWithClients ? 'Shared' : 'Private'}</span>
                  <Switch checked={!!d.sharedWithClients} onCheckedChange={() => toggleShared(d)} data-testid={`library-share-toggle-${d.id}`} />
                </div>
                <Button size="sm" variant="ghost" onClick={() => download(d)} data-testid={`library-download-${d.id}`} aria-label="Download">
                  <Download className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(d)} data-testid={`library-delete-${d.id}`} aria-label="Delete">
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
